import {
  Heading,
  Input,
  InputGroup,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Search } from "./Search";
import { DisciplineClient, SignupClient } from "../../../../Api/client/CompetitionClient";
import { Role, useSessionState } from "../../../../Session/SessionService";
import { ModifySignupModal } from "./ModifySignupModal";
import { StartlistExtendedRow } from "../../../../Api/dto/StartlistDto";
import { Discipline_GET } from "../../../../Api/dto/Discipline_GET";
import { useAwaitResponse } from "../../../../Api/HttpRequestHelpers";

export function ModifySignupComponent() {
  const { competitionTag } = useParams();
  const session = useSessionState();

  const [disciplines, isLoadingDisciplines, setDisciplines] = useAwaitResponse(
    [] as Array<Discipline_GET>,
    () => DisciplineClient.getDisciplines(competitionTag!),
    (response) => response.body
  );

  const [searchText, setSearchText] = useState<string>("");
  const [editRow, setEditRow] = useState<StartlistExtendedRow | null>(null);

  const [startlist, setStartlist, startlistIsLoaded, setStartlistIsLoaded] =
    useFutureState<Array<StartlistExtendedRow>>([]);

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (startlistIsLoaded == false && competitionTag != null) {
      SignupClient.getExtendedStartlist(competitionTag).then(
        (startlistRows) => {
          setStartlist(startlistRows);
          setStartlistIsLoaded(true);
        }
      );
    }
  }, [startlistIsLoaded, competitionTag, setStartlist, setStartlistIsLoaded]);

  if (!session.hasAtLeastRole(Role.USER)) {
    return <LoginPromptMessage />;
  } else if (competitionTag == null) {
    return <NotFoundMessage />;
  }

  return (
    <VStack>
      <FilterInput
        value={searchText}
        onChange={(newVal: string) => setSearchText(newVal)}
      />
      <Search
        competitionTag={competitionTag}
        startlist={startlist}
        searchString={searchText}
        setEditRow={setEditRow}
        headerValues={
          isSmallScreen
            ? ["Klass", "Nr", "Löpare"]//,"T-shirt"]
            : [
                "Klass",
                "Nr",
                "Namn",
                "Födelseår",
                "Klubb/Lag",
                //"T-shirt",
                "PlanneradStart",
              ]
        }
        dataMapper={(row: StartlistExtendedRow) => {
          if (isSmallScreen) {
            let groupStr = row.groupName?.trim()
              ? ` [${row.groupName.trim()}]`
              : "";
            return [
              row.disciplineName,
              `${row.startnumber}`,
              `${row.firstname} ${row.lastName}${groupStr}`,
              //row.additionalInfo ?? "",
            ];
          }
          return [
            row.disciplineName,
            `${row.startnumber}`,
            `${row.firstname} ${row.lastName}`,
            `${row.birthYear}`,
            row.groupName ? row.groupName : row.sportTeam,
            //row.additionalInfo ?? "",
            row.startgroupPlannedStartTime
              ? new Date(row.startgroupPlannedStartTime).toLocaleString("sv-SE")
              : "-",
          ];
        }}
      />
      <ModifySignupModal
        competitionTag={competitionTag}
        disciplines={disciplines!}
        editRow={editRow}
        onClose={(reloadSignups: boolean) => {
          setEditRow(null);
          if (reloadSignups) {
            setStartlistIsLoaded(false);
          }
        }}
        onRestoreDeleted={async (signupId: number) => {
          await SignupClient.admin.restoreDeletedSignup(signupId);
        }}
        onUpdate={async (newSignup, signupId) => {
          console.log("Updating signup " + signupId, newSignup);
          return SignupClient.putSignupUpdateRequest(
            competitionTag,
            signupId,
            newSignup
          );
        }}
        onUpdateGroupName={async (
          oldName: string,
          newName: string,
          paymentId: number
        ) => {
          return SignupClient.changeGroupName(
            competitionTag,
            oldName,
            newName,
            paymentId
          );
        }}
        onDelete={async (signupId: number) => {
          return SignupClient.admin.deleteSignup(signupId);
        }}
      />
    </VStack>
  );
}

function LoginPromptMessage() {
  return (
    <>
      <Heading>Logga in!</Heading>
      <Text textColor="red" fontWeight={"bold"}>
        Du måste vara inloggad för att handera dina anmälningar.
      </Text>
    </>
  );
}

function NotFoundMessage() {
  return (
    <>
      <Heading>404 Not found</Heading>
      <Text textColor="red" fontWeight={"bold"}>
        Sidan du försöker nå kunde inte hittas.
      </Text>
    </>
  );
}

function FilterInput(props: {
  value: string;
  onChange: (newValue: string) => void;
}) {
  return (
    <InputGroup margin={"30px"} width={"auto"} background={"white"}>
      <Input
        id={"searchInput_" + "id"}
        borderColor="black"
        type="text"
        value={props.value ?? ""}
        placeholder="Sök"
        style={{ width: "auto" }}
        minW={"150px"}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        width={"md"}
      />
    </InputGroup>
  );
}

export function useFutureState<S>(
  initialState: S | (() => S),
  initialIsLoaded?: boolean
): [
  S,
  React.Dispatch<React.SetStateAction<S>>,
  boolean,
  React.Dispatch<React.SetStateAction<boolean>>
] {
  const [state, setState] = useState<S>(initialState);
  const [isLoaded, setIsLoaded] = useState(initialIsLoaded ?? false);
  return [state, setState, isLoaded, setIsLoaded];
}
